// V5 mobile UX pass — visual confirmation for touch taps. On desktop the
// cursor is the confirmation (the player sees where they clicked); on
// touch the finger covers the destination, so a brief on-screen ring is
// the only feedback that the tap registered as a command and not a stray
// gesture. Three kinds:
//   - 'move'   : own squad receives a move order (cyan)
//   - 'attack' : own squad receives an attack-move / implicit-attack (red)
//   - 'select' : tap-toggle (yellow)
//
// Pure DOM: a single transient <div> with a CSS keyframe animation that
// self-removes via setTimeout. No runtime state, no Phaser dependency,
// no allocator churn (one element per tap; GC handles the rest).
//
// Coordinates are CLIENT-space (clientX/clientY of the originating
// PointerEvent) — the helper does the canvas-rect arithmetic so the
// caller only needs to pass the in-canvas pointer position plus the
// canvas element itself.

const STYLE_ID = 'tap-pulse-styles';
const PULSE_MS = 460;
const PULSE_PX = 56;

export type TapPulseKind = 'move' | 'attack' | 'select';

export function showTapPulse(
  canvas: HTMLCanvasElement | null | undefined,
  pointerX: number,
  pointerY: number,
  kind: TapPulseKind,
): void {
  if (typeof document === 'undefined') return;
  if (canvas === null || canvas === undefined) return;
  injectStyles();
  const rect = canvas.getBoundingClientRect();
  const cssX = rect.left + pointerX;
  const cssY = rect.top + pointerY;
  const ring = document.createElement('div');
  ring.className = `tap-pulse tap-pulse--${kind}`;
  ring.style.left = `${cssX - PULSE_PX / 2}px`;
  ring.style.top = `${cssY - PULSE_PX / 2}px`;
  document.body.appendChild(ring);
  // Self-remove after the animation completes. setTimeout is cheaper and
  // more reliable than animationend across browsers (especially older
  // iOS Safari where the event sometimes drops on rapid taps).
  window.setTimeout(() => {
    ring.remove();
  }, PULSE_MS);
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  if (typeof document === 'undefined') return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.tap-pulse {
  position: fixed;
  width: ${PULSE_PX}px;
  height: ${PULSE_PX}px;
  border-radius: 50%;
  border: 2px solid #6ad8ff;
  pointer-events: none;
  z-index: 26;
  animation: tap-pulse-grow ${PULSE_MS}ms ease-out forwards;
  box-sizing: border-box;
  will-change: transform, opacity;
}
.tap-pulse--move   { border-color: #6ad8ff; box-shadow: 0 0 12px rgba(106,216,255,0.55); }
.tap-pulse--attack { border-color: #ff8060; box-shadow: 0 0 12px rgba(255,128,96,0.55); }
.tap-pulse--select { border-color: #f5e76e; box-shadow: 0 0 10px rgba(245,231,110,0.45); }
@keyframes tap-pulse-grow {
  0%   { transform: scale(0.55); opacity: 1; }
  70%  { opacity: 0.7; }
  100% { transform: scale(1.75); opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .tap-pulse { animation-duration: ${Math.round(PULSE_MS / 2)}ms; }
}
`;
  document.head.appendChild(style);
}
